import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addJobFavorite,
  getFavoriteJobs,
  getJobFavoriteIds,
  removeJobFavorite
} from '../../utils/api/jobFavorites';
import { getErrorMessage } from '../../utils/api/errors.js';

const normalizeFavoriteJob = (job = {}) => {
  const skills = Array.isArray(job.skills) ? job.skills : [];
  return {
    id: job.id,
    title: job.title || '',
    company: job.company || '',
    location: job.location || '',
    source: job.source || 'Company',
    description: job.description || '',
    postedDate: job.posted_date || job.postedDate || job.created_at || job.createdAt || null,
    salary: job.salary_range || job.salaryRange || '',
    jobType: job.job_type || job.jobType || '',
    jobMode: job.job_mode || job.jobMode || '',
    experience: job.experience_required || job.experienceRequired || '',
    status: job.status || 'pending',
    jobscribeId: job.jobscribe_id || job.jobscribeId || '',
    importedFromAdminJobId: job.imported_from_admin_job_id || job.importedFromAdminJobId || null,
    tags: skills
  };
};

const extractData = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (payload.ids !== undefined || payload.jobs !== undefined) return payload;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

export const fetchJobFavoriteIds = createAsyncThunk(
  'jobFavorites/fetchIds',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getJobFavoriteIds();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load favorites'));
    }
  }
);

export const fetchFavoriteJobs = createAsyncThunk(
  'jobFavorites/fetchJobs',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getFavoriteJobs(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load favorite jobs'));
    }
  }
);

export const addFavoriteJobThunk = createAsyncThunk(
  'jobFavorites/add',
  async (jobId, { rejectWithValue }) => {
    try {
      await addJobFavorite(jobId);
      return jobId;
    } catch (error) {
      return rejectWithValue({ jobId, message: error.message || 'Failed to add favorite' });
    }
  }
);

export const removeFavoriteJobThunk = createAsyncThunk(
  'jobFavorites/remove',
  async (jobId, { rejectWithValue }) => {
    try {
      await removeJobFavorite(jobId);
      return jobId;
    } catch (error) {
      return rejectWithValue({ jobId, message: error.message || 'Failed to remove favorite' });
    }
  }
);

const initialState = {
  ids: [],
  items: [],
  pagination: null,
  loading: false,
  error: null
};

const jobFavoritesSlice = createSlice({
  name: 'jobFavorites',
  initialState,
  reducers: {
    clearJobFavoritesError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobFavoriteIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobFavoriteIds.fulfilled, (state, action) => {
        state.loading = false;
        const data = extractData(action.payload);
        state.ids = Array.isArray(data?.ids) ? data.ids.map(Number) : [];
      })
      .addCase(fetchJobFavoriteIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchFavoriteJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFavoriteJobs.fulfilled, (state, action) => {
        state.loading = false;
        const data = extractData(action.payload);
        const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
        state.items = jobs.map(normalizeFavoriteJob);
        state.pagination = data?.pagination || null;
        state.ids = state.items.map((j) => j.id);
      })
      .addCase(fetchFavoriteJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addFavoriteJobThunk.pending, (state, action) => {
        const jobId = action.meta.arg;
        if (!state.ids.includes(jobId)) {
          state.ids.push(jobId);
        }
      })
      .addCase(addFavoriteJobThunk.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(addFavoriteJobThunk.rejected, (state, action) => {
        const jobId = action.meta.arg;
        state.ids = state.ids.filter((id) => id !== jobId);
        state.error = action.payload?.message || 'Failed to add favorite';
      })
      .addCase(removeFavoriteJobThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(removeFavoriteJobThunk.fulfilled, (state, action) => {
        const jobId = action.payload;
        state.ids = state.ids.filter((id) => id !== jobId);
        state.items = state.items.filter((j) => j.id !== jobId);
        state.error = null;
      })
      .addCase(removeFavoriteJobThunk.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to remove favorite';
      });
  }
});

export const { clearJobFavoritesError } = jobFavoritesSlice.actions;
export default jobFavoritesSlice.reducer;
